import { useState } from "react";
import { getStoredEmail } from "../emailStorage";

interface EmailLinkFormProps {
  apiUrl: string;
  /** The submit button's label, e.g. "Email me a sign-in link". */
  submitLabel: string;
}

/**
 * Asks for an address and has /api/login/email send a one-time sign-in link
 * to it. Used by the login page and by the "save your account" modal: both
 * are the same act, since which account the link opens is decided when it is
 * clicked (app/routers/login.py), and an anonymous browser that clicks it
 * keeps its own designs under that address.
 */
export function EmailLinkForm({ apiUrl, submitLabel }: EmailLinkFormProps) {
  const [email, setEmail] = useState(getStoredEmail);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [sentTo, setSentTo] = useState<{ email: string; minutes: number } | null>(null);

  const send = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError("");
    try {
      const response = await fetch(`${apiUrl}/api/login/email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(typeof data.detail === "string" ? data.detail : "Something went wrong. Try again.");
        return;
      }
      setSentTo({ email: email.trim(), minutes: data.valid_minutes });
    } catch {
      setError("Couldn't reach the server. Try again.");
    } finally {
      setSending(false);
    }
  };

  if (sentTo) {
    return (
      <p className="email-link-sent">
        Check your inbox. We sent a sign-in link to <strong>{sentTo.email}</strong>.
        It works once, for the next {sentTo.minutes} minutes.
        {" "}
        <a href="#" onClick={(e) => { e.preventDefault(); setSentTo(null); }}>
          Use a different email
        </a>
      </p>
    );
  }

  return (
    <form onSubmit={send}>
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        aria-label="Email address"
        autoComplete="email"
      />
      {error && <small className="email-link-error">{error}</small>}
      <button type="submit" disabled={sending}>
        {sending ? "Sending..." : submitLabel}
      </button>
    </form>
  );
}
